/**
 * Crosshair — small center-screen dot shown only while the pointer is locked
 * (FPS aim mode). Hidden by default. Owned by HudManager / GameClient and
 * driven by pointer-lock state changes.
 *
 * Defensive guard: if a caller invokes `setVisible(true)` but the document
 * has not actually acquired pointer lock (e.g. `requestPointerLock()` was
 * silently denied by an iframe sandbox or browser policy), the crosshair
 * stays hidden. We also subscribe to `pointerlockchange` directly so the
 * crosshair force-hides on lock loss even if no caller updates us.
 */

export class Crosshair {
  private el: HTMLDivElement;
  private intendedVisible = false;
  private readonly pointerLockChangeHandler = () => this.applyVisibility();

  constructor(hudRoot: HTMLElement) {
    this.el = document.createElement('div') as HTMLDivElement;
    this.el.className = 'crosshair';
    this.el.style.display = 'none';
    hudRoot.appendChild(this.el);
    document.addEventListener('pointerlockchange', this.pointerLockChangeHandler);
  }

  setVisible(visible: boolean): void {
    this.intendedVisible = visible;
    this.applyVisibility();
  }

  private applyVisibility(): void {
    const locked = document.pointerLockElement !== null;
    this.el.style.display = this.intendedVisible && locked ? 'block' : 'none';
  }

  dispose(): void {
    document.removeEventListener('pointerlockchange', this.pointerLockChangeHandler);
    this.el.remove();
  }
}
